<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import AdaptiveSelect from 'dashboard/components-next/adaptive-select/AdaptiveSelect.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import BaseTable from 'dashboard/components-next/table/BaseTable.vue';
import BaseTableCell from 'dashboard/components-next/table/BaseTableCell.vue';
import BaseTableRow from 'dashboard/components-next/table/BaseTableRow.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import InvoiceSupportingDetails from './InvoiceSupportingDetails.vue';

const props = defineProps({
  snapshot: { type: Object, required: true },
  actionInProgress: { type: Boolean, default: false },
});

defineEmits(['refresh', 'unsuppressEmail']);

const { locale, t } = useI18n();
const search = ref('');
const invoiceStatus = ref('all');
const data = computed(() => props.snapshot?.payload?.data || {});
const kind = computed(
  () => data.value.kind || props.snapshot?.payload?.metadata?.kind || 'generic'
);
const servers = computed(() =>
  Array.isArray(data.value.servers) ? data.value.servers : []
);
const invoices = computed(() =>
  Array.isArray(data.value.invoices) ? data.value.invoices : []
);
const emails = computed(() =>
  Array.isArray(data.value.emails) ? data.value.emails : []
);
const isLoading = computed(() =>
  ['pending', 'refreshing'].includes(props.snapshot.status)
);
const isFailed = computed(
  () => props.snapshot.status === 'failed' || props.snapshot.result === 'error'
);
const isPartial = computed(
  () =>
    props.snapshot.result === 'partial' ||
    props.snapshot.payload?.result === 'partial'
);
const auxiliaryTruncated = computed(
  () => props.snapshot.payload?.metadata?.auxiliary_truncated === true
);
const normalizedSearch = computed(() =>
  search.value.trim().toLocaleLowerCase()
);
const visibleServers = computed(() =>
  servers.value.filter(server =>
    [server.name, server.url, server.datacenter, server.license_type]
      .filter(Boolean)
      .join(' ')
      .toLocaleLowerCase()
      .includes(normalizedSearch.value)
  )
);
const visibleInvoices = computed(() =>
  invoices.value.filter(invoice => {
    const matchesStatus =
      invoiceStatus.value === 'all' || invoice.status === invoiceStatus.value;
    const matchesSearch = [
      invoice.number,
      invoice.id,
      invoice.description,
      invoice.subscription_id,
    ]
      .filter(Boolean)
      .join(' ')
      .toLocaleLowerCase()
      .includes(normalizedSearch.value);
    return matchesStatus && matchesSearch;
  })
);
const visibleEmails = computed(() =>
  emails.value.filter(email =>
    [email.to_email, email.from_email, email.subject, email.delivery_status]
      .filter(Boolean)
      .join(' ')
      .toLocaleLowerCase()
      .includes(normalizedSearch.value)
  )
);
const invoiceStatusOptions = computed(() => [
  {
    value: 'all',
    label: t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.INVOICE_STATUS.all'),
  },
  {
    value: 'paid',
    label: t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.INVOICE_STATUS.paid'),
  },
  {
    value: 'open',
    label: t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.INVOICE_STATUS.open'),
  },
  {
    value: 'void',
    label: t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.INVOICE_STATUS.void'),
  },
  {
    value: 'uncollectible',
    label: t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.INVOICE_STATUS.uncollectible'),
  },
  {
    value: 'draft',
    label: t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.INVOICE_STATUS.draft'),
  },
]);
const snapshotTitles = computed(() => ({
  contracted_servers: t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.SERVERS_TITLE'),
  received_invoices: t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.INVOICES_TITLE'),
  received_emails: t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.EMAILS_TITLE'),
}));
const snapshotTitle = computed(() =>
  kind.value in snapshotTitles.value
    ? snapshotTitles.value[kind.value]
    : t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.GENERIC_TITLE')
);
const serverHeaders = computed(() => [
  t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.SERVER'),
  t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.STATUS'),
  t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.INSTANCES'),
  t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.EXPIRES_AT'),
]);
const invoiceHeaders = computed(() => [
  t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.INVOICE'),
  t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.STATUS'),
  t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.AMOUNT'),
  t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.CREATED_AT'),
  t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.ACTIONS'),
]);
const emailHeaders = computed(() => [
  t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.EMAIL'),
  t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.SUBJECT'),
  t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.STATUS'),
  t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.LAST_EVENT_AT'),
]);

const formatMoney = (amount, currency, precision) => {
  const numericAmount = Number(amount);
  if (
    (typeof amount !== 'number' && typeof amount !== 'string') ||
    amount === null ||
    amount === undefined ||
    String(amount).trim() === '' ||
    !Number.isSafeInteger(numericAmount) ||
    !currency
  ) {
    return t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.NOT_AVAILABLE');
  }
  const currencyCode = String(currency).toUpperCase();
  try {
    const formatter = new Intl.NumberFormat(locale.value.replace('_', '-'), {
      style: 'currency',
      currency: currencyCode,
    });
    const defaultDecimals = ['ISK', 'UGX'].includes(currencyCode)
      ? 2
      : formatter.resolvedOptions().maximumFractionDigits;
    const decimals =
      Number.isInteger(precision) && precision >= 0 && precision <= 3
        ? precision
        : defaultDecimals;
    return formatter.format(numericAmount / 10 ** decimals);
  } catch {
    return t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.NOT_AVAILABLE');
  }
};

const formatDate = value => {
  if (!value) return t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.NOT_AVAILABLE');
  const date = new Date(typeof value === 'number' ? value * 1000 : value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(locale.value.replace('_', '-'), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const safeHttpUrl = value => {
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol) &&
      !url.username &&
      !url.password
      ? url.toString()
      : '';
  } catch {
    return '';
  }
};

const serverStatus = server =>
  server.online === true ||
  ['online', 'running'].includes(
    String(server.online_status || '').toLowerCase()
  )
    ? t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.ONLINE')
    : t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.OFFLINE');
const invoiceStatusLabel = status => {
  const option = invoiceStatusOptions.value.find(item => item.value === status);
  return (
    option?.label ||
    t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.INVOICE_STATUS.unknown')
  );
};
const emailStatusLabel = status => {
  switch (status) {
    case 'scheduled':
      return t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.EMAIL_STATUS.scheduled');
    case 'sent':
      return t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.EMAIL_STATUS.sent');
    case 'delivered':
      return t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.EMAIL_STATUS.delivered');
    case 'delayed':
      return t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.EMAIL_STATUS.delayed');
    case 'bounced':
      return t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.EMAIL_STATUS.bounced');
    case 'failed':
      return t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.EMAIL_STATUS.failed');
    case 'complained':
      return t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.EMAIL_STATUS.complained');
    case 'suppressed':
      return t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.EMAIL_STATUS.suppressed');
    case 'canceled':
      return t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.EMAIL_STATUS.canceled');
    default:
      return t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.EMAIL_STATUS.unknown');
  }
};
const hasSuppression = computed(() =>
  emails.value.some(email =>
    ['bounced', 'complained', 'suppressed'].includes(email.delivery_status)
  )
);
const instanceUsage = server =>
  t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.INSTANCE_USAGE', {
    online: server.instances_online || 0,
    limit: server.instances_limit || 0,
  });
</script>

<template>
  <div class="h-full min-w-0 overflow-y-auto bg-n-surface-1 p-4">
    <LoadingState
      v-if="isLoading"
      :message="$t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.LOADING')"
      class="flex min-h-40 items-center justify-center"
    />

    <div v-else class="flex min-w-0 flex-col gap-4">
      <div
        class="flex flex-wrap items-start justify-between gap-3 border-b border-n-weak pb-4"
      >
        <div class="min-w-0">
          <h2 class="m-0 text-heading-2 text-n-slate-12">
            {{ snapshotTitle }}
          </h2>
          <p
            v-if="data.customer"
            class="m-0 mt-1 text-label-small leading-4 text-n-slate-11"
          >
            {{
              data.customer.name ||
              data.customer.email ||
              data.customer.phone_number
            }}
          </p>
        </div>
        <Button
          :label="$t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.REFRESH')"
          icon="i-lucide-refresh-cw"
          size="sm"
          @click="$emit('refresh')"
        />
      </div>

      <div
        v-if="isFailed"
        class="rounded-lg border border-n-ruby-8 bg-n-ruby-3 p-4 text-body-main text-n-ruby-11"
        role="alert"
      >
        {{
          snapshot.error_message || $t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.ERROR')
        }}
      </div>

      <div
        v-else-if="isPartial || auxiliaryTruncated"
        class="rounded-lg border border-n-amber-7 bg-n-amber-2 p-4 text-body-main text-n-amber-11"
        role="status"
      >
        {{
          auxiliaryTruncated
            ? $t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.AUXILIARY_TRUNCATED')
            : $t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.PARTIAL')
        }}
      </div>

      <template v-else-if="kind === 'contracted_servers'">
        <Input
          v-model="search"
          :label="$t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.SEARCH_SERVERS')"
          :placeholder="
            $t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.SEARCH_SERVERS_PLACEHOLDER')
          "
        />
        <BaseTable
          :headers="serverHeaders"
          :items="visibleServers"
          :no-data-message="$t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.NO_SERVERS')"
        >
          <template #row="{ items }">
            <BaseTableRow
              v-for="server in items"
              :key="server.id || server.url"
              :item="server"
            >
              <BaseTableCell>
                <p class="m-0 font-medium text-n-slate-12">
                  {{ server.name || server.url }}
                </p>
                <p class="m-0 text-label-small text-n-slate-11">
                  {{ server.datacenter || server.license_type }}
                </p>
              </BaseTableCell>
              <BaseTableCell>{{ serverStatus(server) }}</BaseTableCell>
              <BaseTableCell>{{ instanceUsage(server) }}</BaseTableCell>
              <BaseTableCell>{{ formatDate(server.expires_at) }}</BaseTableCell>
            </BaseTableRow>
          </template>
        </BaseTable>
      </template>

      <template v-else-if="kind === 'received_invoices'">
        <div class="grid min-w-0 gap-3 sm:grid-cols-2">
          <Input
            v-model="search"
            :label="$t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.SEARCH_INVOICES')"
            :placeholder="
              $t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.SEARCH_INVOICES_PLACEHOLDER')
            "
          />
          <AdaptiveSelect
            v-model="invoiceStatus"
            :options="invoiceStatusOptions"
            :display-label="
              $t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.FILTER_STATUS')
            "
          />
        </div>
        <BaseTable
          :headers="invoiceHeaders"
          :items="visibleInvoices"
          :no-data-message="$t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.NO_INVOICES')"
        >
          <template #row="{ items }">
            <BaseTableRow
              v-for="invoice in items"
              :key="invoice.id"
              :item="invoice"
            >
              <BaseTableCell>
                <p class="m-0 font-medium text-n-slate-12">
                  {{ invoice.number || invoice.id }}
                </p>
                <p class="m-0 text-label-small text-n-slate-11">
                  {{ invoice.description }}
                </p>
              </BaseTableCell>
              <BaseTableCell>
                {{ invoiceStatusLabel(invoice.status) }}
              </BaseTableCell>
              <BaseTableCell>
                {{
                  formatMoney(
                    invoice.amount_due,
                    invoice.currency,
                    invoice.amount_precision
                  )
                }}
              </BaseTableCell>
              <BaseTableCell>
                {{ formatDate(invoice.created_at) }}
              </BaseTableCell>
              <BaseTableCell align="end">
                <a
                  v-if="safeHttpUrl(invoice.hosted_url)"
                  :href="safeHttpUrl(invoice.hosted_url)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="font-medium text-n-brand hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                >
                  {{ $t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.OPEN_INVOICE') }}
                </a>
              </BaseTableCell>
            </BaseTableRow>
          </template>
        </BaseTable>
        <InvoiceSupportingDetails :resources="data.resources" />
      </template>

      <template v-else-if="kind === 'received_emails'">
        <div class="flex min-w-0 flex-wrap items-end gap-3">
          <Input
            v-model="search"
            class="min-w-52 flex-1"
            :label="$t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.SEARCH_EMAILS')"
            :placeholder="
              $t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.SEARCH_EMAILS_PLACEHOLDER')
            "
          />
          <Button
            v-if="hasSuppression"
            :label="$t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.UNSUPPRESS')"
            icon="i-lucide-shield-check"
            color="ruby"
            variant="outline"
            :is-loading="props.actionInProgress"
            :disabled="props.actionInProgress"
            @click="$emit('unsuppressEmail')"
          />
        </div>
        <BaseTable
          :headers="emailHeaders"
          :items="visibleEmails"
          :no-data-message="$t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.NO_EMAILS')"
        >
          <template #row="{ items }">
            <BaseTableRow
              v-for="email in items"
              :key="`${email.resend_email_id}:${email.to_email}`"
              :item="email"
            >
              <BaseTableCell>
                <p class="m-0 font-medium text-n-slate-12">
                  {{ email.to_email }}
                </p>
                <p class="m-0 text-label-small text-n-slate-11">
                  {{ email.from_email }}
                </p>
              </BaseTableCell>
              <BaseTableCell>
                <p class="m-0 text-n-slate-12">{{ email.subject }}</p>
                <p
                  v-if="email.bounce_message"
                  class="m-0 mt-1 text-label-small text-n-ruby-11"
                >
                  {{ email.bounce_message }}
                </p>
              </BaseTableCell>
              <BaseTableCell>
                {{ emailStatusLabel(email.delivery_status) }}
              </BaseTableCell>
              <BaseTableCell>
                {{ formatDate(email.last_status_at) }}
              </BaseTableCell>
            </BaseTableRow>
          </template>
        </BaseTable>
      </template>

      <div
        v-else
        class="rounded-lg border border-n-weak p-4 text-body-main text-n-slate-11"
      >
        {{ $t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.UNSUPPORTED') }}
      </div>
    </div>
  </div>
</template>
